import { yupResolver } from '@hookform/resolvers/yup';
import classNames from 'classnames';
import React, { FC, useEffect, useRef, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { SubmitHandler, useForm } from 'react-hook-form';

import { filesSizeLimitInBytes } from '@/src/assets/constants/constants';
import { getFormData } from '@/src/assets/helpers/getFormData';
import { useOutsideAlerter } from '@/src/assets/hooks/useOutsideClick';

import Button from '../UI/button/Button';
import { Title } from '../UI/title/Title';
import { CloseIcon } from '../icons/closeIcon/CloseIcon';
import { ErrorIcon } from '../icons/errorIcon/ErrorIcon';
import { LoaderIcon } from '../icons/loaderIcon/LoaderIcon';
import { SuccessIcon } from '../icons/successIcon/SuccessIcon';
import Portal from '../portal/Portal';
import { useModal } from '../providers/modalContext/ModalContext';
import { supportFormSchema } from '../screens/Support/yupSchema';

import styles from './SupportForm.module.scss';

interface ModalProps {
    className?: string;
    isOpened: boolean;
    themesTitles: string[];
    wrapperClassName: string;
}

interface Inputs {
    name: string;
    email: string;
    subject?: string;
    siteUrl?: string;
    theme: string;
    request: string;
}

interface supportFormState {
    isLoading: boolean;
    submitted: boolean;
    isEmailSent: boolean;
}

const SupportForm: FC<ModalProps> = ({ className, isOpened, themesTitles, wrapperClassName }) => {
    const [supportFormState, setSupportFormState] = useState<supportFormState>({
        isLoading: false,
        submitted: false,
        isEmailSent: false,
    });

    const {
        register,
        handleSubmit,
        formState: { errors },
        reset,
        setFocus,
    } = useForm<Inputs>({
        mode: 'all',
        resolver: yupResolver(supportFormSchema),
    });

    const { acceptedFiles, getRootProps, fileRejections, getInputProps } = useDropzone({
        accept: {
            'image/jpeg': [],
            'image/png': [],
            'image/jpg': [],
            'application/pdf': [],
            'application/zip': [],
        },
        maxFiles: 1,
        minSize: 0,
        maxSize: filesSizeLimitInBytes,
    });

    useEffect(() => {
        const timeout = setTimeout(() => {
            setFocus('name');
        }, 0);

        return () => {
            clearTimeout(timeout);
        };
    }, []);

    const { handleCloseModal } = useModal();

    const handleClose = () => {
        isOpened && handleCloseModal();
    };

    const formRef = useRef<HTMLFormElement>(null);
    const formResultRef = useRef<HTMLDivElement>(null);

    useOutsideAlerter(formRef, handleClose);
    useOutsideAlerter(formResultRef, handleClose);

    const postData = async (data: Inputs) => {
        setSupportFormState((prev) => {
            return {
                ...prev,
                isLoading: true,
            };
        });

        const formData = getFormData(data, acceptedFiles);

        const response = await fetch('/api/mail', {
            method: 'POST',
            body: formData,
        });

        setSupportFormState(() => {
            return {
                submitted: true,
                isEmailSent: response.ok,
                isLoading: false,
            };
        });

        reset();
    };

    const onSubmit: SubmitHandler<Inputs> = (data) => {
        postData(data);
    };

    const isValidationErrors = !!(fileRejections.length || Object.keys(errors).length);

    return (
        <Portal wrapperId="react-portal-modal-container">
            <div className={classNames(styles.modal__wrapper, wrapperClassName)}>
                {supportFormState.submitted ? (
                    <div
                        className={classNames(styles.formSubmissionResult, className)}
                        ref={formResultRef}>
                        <button
                            aria-label="Close form button"
                            onClick={handleClose}
                            className={classNames(styles.close, 'button')}>
                            <CloseIcon />
                        </button>
                        {supportFormState.isEmailSent ? <SuccessIcon /> : <ErrorIcon />}

                        <Title
                            text={
                                supportFormState.isEmailSent
                                    ? 'Success! Your message has been sent!'
                                    : 'Oops! Something went wrong!'
                            }
                            size="sm"
                            className={styles.formSubmissionResult__title}
                        />

                        <p className={styles.formSubmissionResult__desc}>
                            {supportFormState.isEmailSent
                                ? 'Thank you! We will get to you as soon as possible.'
                                : 'There was an error trying to send your message. Please try again later.'}
                        </p>
                        <Button buttonUrl={'/'} ariaLabel="Back to home page link" color="standard">
                            Ok, Thanks!
                        </Button>
                    </div>
                ) : (
                    <form
                        className={classNames(styles.modal, className)}
                        onSubmit={handleSubmit(onSubmit)}
                        noValidate
                        ref={formRef}>
                        <button
                            aria-label="Close form button"
                            onClick={handleClose}
                            className={classNames(styles.close, 'button')}>
                            <CloseIcon />
                        </button>
                        <div>
                            <Title text="Submit Ticket" size="sm" className={styles.heading} />
                            <p className={classNames('excerpt', styles.excerpt)}>
                                Estimated wait time <span>2+ business days.</span>
                            </p>
                        </div>

                        <div className={styles.inputsContainer}>
                            <div className={styles.inputWrapper}>
                                <label htmlFor="support-name" className={styles.lable}>
                                    Name
                                </label>
                                <input
                                    type="text"
                                    id="support-name"
                                    placeholder="Name"
                                    className={classNames(styles.input, {
                                        [styles['input--error']]: errors.name,
                                    })}
                                    aria-label="Name"
                                    {...register('name', { required: true })}
                                />
                                {errors.name && (
                                    <p className={styles.error}>{errors.name.message}</p>
                                )}
                            </div>
                            <div className={styles.inputWrapper}>
                                <label htmlFor="support-email" className={styles.lable}>
                                    E-mail
                                </label>
                                <input
                                    type="email"
                                    id="support-email"
                                    placeholder="E-mail"
                                    className={classNames(styles.input, {
                                        [styles['input--error']]: errors.email,
                                    })}
                                    aria-label="E-mail"
                                    {...register('email', { required: true })}
                                />
                                {errors.email && (
                                    <p className={styles.error}>{errors.email.message}</p>
                                )}
                            </div>
                            <div
                                className={classNames(
                                    styles.inputWrapper,
                                    styles[`inputWrapper--small`]
                                )}>
                                <label htmlFor="support-subject" className={styles.lable}>
                                    Subject
                                </label>
                                <input
                                    type="text"
                                    id="support-subject"
                                    placeholder="Subject"
                                    className={classNames(styles.input, {
                                        [styles['input--error']]: errors.subject,
                                    })}
                                    aria-label="Subject"
                                    {...register('subject')}
                                />
                                {errors.subject && (
                                    <p className={styles.error}>{errors.subject.message}</p>
                                )}
                            </div>
                            <div
                                className={classNames(
                                    styles.inputWrapper,
                                    styles[`inputWrapper--small`]
                                )}>
                                <label htmlFor="support-theme" className={styles.lable}>
                                    Theme
                                </label>
                                <select
                                    id="support-theme"
                                    defaultValue={'default'}
                                    placeholder="Theme"
                                    className={classNames(styles.input, styles.select, {
                                        [styles['input--error']]: errors.theme,
                                    })}
                                    aria-label="Theme"
                                    {...register('theme', { required: true })}>
                                    <option value="default" disabled>
                                        Select Theme
                                    </option>
                                    {themesTitles.map((themeTitle, index) => {
                                        return (
                                            <option value={themeTitle} key={index}>
                                                {themeTitle}
                                            </option>
                                        );
                                    })}
                                </select>
                                {errors.theme && (
                                    <p className={styles.error}>{errors.theme.message}</p>
                                )}
                            </div>
                            <div
                                className={classNames(
                                    styles.inputWrapper,
                                    styles[`inputWrapper--small`]
                                )}>
                                <label htmlFor="support-siteUrl" className={styles.lable}>
                                    Site URL
                                </label>
                                <input
                                    type="text"
                                    id="support-siteUrl"
                                    placeholder="Site URL"
                                    className={classNames(styles.input, {
                                        [styles['input--error']]: errors.siteUrl,
                                    })}
                                    aria-label="Site URL"
                                    {...register('siteUrl')}
                                />
                                {errors.siteUrl && (
                                    <p className={styles.error}>{errors.siteUrl.message}</p>
                                )}
                            </div>
                            <div className={styles.area__wrapper}>
                                <label htmlFor="support-area" className={styles.lable}>
                                    Request
                                </label>
                                <textarea
                                    id="support-area"
                                    placeholder="Your Request"
                                    className={classNames(styles.area, {
                                        [styles['area--error']]: errors.request,
                                    })}
                                    aria-label="Text area for your request"
                                    {...register('request', { required: true })}
                                />
                                {errors.request && (
                                    <p className={styles.error}>{errors.request.message}</p>
                                )}
                            </div>
                            <section className={styles.dropzone__container}>
                                <p className={styles.dropzone__note}>
                                    <strong>Please, read this before attaching a file:</strong> If
                                    you want to add more than one file, please, create a ZIP archive
                                    containing these files. Please,
                                    <strong> do not attach</strong> a ZIP archive of the theme, as
                                    email client may block messages with such attachments.
                                </p>
                                <div {...getRootProps({ className: 'dropzone' })}>
                                    <input {...getInputProps()} />
                                    {!acceptedFiles.length ? (
                                        <h6 className={styles.dropzone__heading}>
                                            {fileRejections.length ? (
                                                <span className={styles['dropzone__error']}>
                                                    Error:
                                                </span>
                                            ) : (
                                                'Add File'
                                            )}
                                        </h6>
                                    ) : (
                                        <h6 className={styles.dropzone__delected}>
                                            Selected file:{' '}
                                            {acceptedFiles.map((file) => `${file.name} `)}
                                        </h6>
                                    )}
                                    {!!fileRejections.length &&
                                        fileRejections.map(({ file, errors }, i) => {
                                            return (
                                                <div key={i} className={styles.dropzone__errors}>
                                                    {errors.map((e) => (
                                                        <div
                                                            key={e.code}
                                                            className={styles.dropzone__error}>
                                                            <span
                                                                className={
                                                                    styles[
                                                                        'dropzone__errorFileName'
                                                                    ]
                                                                }>
                                                                {`${file.name}:`}
                                                            </span>
                                                            <span>{` ${e.message}`}</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            );
                                        })}
                                    <p className={styles.dropzone__excerpt}>
                                        Allowed file formats: jpeg, png, jpg, pdf and zip. Maximum
                                        upload file size: 4MB
                                    </p>
                                </div>
                            </section>
                        </div>
                        <button
                            type="submit"
                            aria-label="Submit Request"
                            color="gradient"
                            className={classNames(styles.button, {
                                [styles[`button--isLoading`]]: supportFormState.isLoading,
                                [styles[`button--locked`]]: isValidationErrors,
                            })}>
                            {supportFormState.isLoading ? <LoaderIcon /> : 'Submit Request'}
                        </button>
                    </form>
                )}
            </div>
        </Portal>
    );
};

export { SupportForm };
